import { Theme } from '@material-ui/core/styles';
import { makeStyles } from '@material-ui/styles';
import clsx from 'clsx';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { Label, MoneyAmountValue, SecondaryTitle, Space } from '@/components';
import {
  ApiTransactionAPIModelFlowDirectionEnum as TransactionAPIModelFlowDirectionEnum,
  ApiTransactionAPIModelStatusEnum as TransactionAPIModelStatusEnum,
} from '@/generated/api/fps/core';
import { MoneyType } from '@/model/common';

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    flexGrow: 1,
    minWidth: 0,
    alignItems: 'flex-end',
  },
  amountValue: {
    fontWeight: 500,
    alignItems: 'flex-start',
  },
  currencyValue: {
    fontSize: '11px',
    lineHeight: '18px',
  },
  amountError: {
    color: theme.colors.error,
  },
  amountPositiveSuccess: {
    color: theme.colors.accent,
  },
  labelStatusFailed: {
    color: theme.colors.error,
  },
  labelStatusProgress: {
    color: '#ED6F26',
  },
}));

interface TransactionStatusLabelProps {
  'data-test'?: string;
  value: TransactionAPIModelStatusEnum;
}

export const TransactionStatusLabel: React.FC<TransactionStatusLabelProps> = ({ value, 'data-test': dataTest }) => {
  const classes = useStyles();
  switch (value) {
    case TransactionAPIModelStatusEnum.Cancelled:
    case TransactionAPIModelStatusEnum.Failed:
      return (
        <Label data-test={dataTest}>
          <span className={classes.labelStatusFailed}>
            <FormattedMessage id={`demo.components.transactionStatus.${value}`} />
          </span>
        </Label>
      );
    case TransactionAPIModelStatusEnum.Frozen:
    case TransactionAPIModelStatusEnum.InProgress:
    case TransactionAPIModelStatusEnum.TakenInNotSent:
    case TransactionAPIModelStatusEnum.TakenOut:
    case TransactionAPIModelStatusEnum.Initiated:
    case TransactionAPIModelStatusEnum.Prepared:
      return (
        <Label data-test={dataTest}>
          <FormattedMessage id="demo.components.transactionStatus.InProgress" />
        </Label>
      );
    case TransactionAPIModelStatusEnum.Finished:
    case TransactionAPIModelStatusEnum.Unknown:
    default:
      return <></>;
  }
};

export interface TransactionAmountProps {
  'data-test'?: string;
  value: {
    status: TransactionAPIModelStatusEnum;
    flowDirection: TransactionAPIModelFlowDirectionEnum;
    amount: MoneyType;
  };
}

export const TransactionAmount: React.FC<TransactionAmountProps> = ({ value, 'data-test': dataTest }) => {
  const classes = useStyles();
  const positive = value.flowDirection === TransactionAPIModelFlowDirectionEnum.Debit;
  return (
    <Space direction="vertical" className={classes.container} size={0}>
      <SecondaryTitle
        data-test={dataTest}
        className={clsx(
          classes.amountValue,
          positive && value.status === TransactionAPIModelStatusEnum.Finished && classes.amountPositiveSuccess,
          // prettier-ignore
          // prettier: FIXME: https://github.com/prettier/prettier/pull/7111
          (value.status === TransactionAPIModelStatusEnum.Failed
            || value.status === TransactionAPIModelStatusEnum.Cancelled)
          && classes.amountError,
        )}
      >
        <MoneyAmountValue
          data-test={dataTest && `${dataTest}-amount`}
          fraction={2}
          withSign
          withCurrency
          value={value.amount}
          currencyClassName={classes.currencyValue}
        />
      </SecondaryTitle>
      <TransactionStatusLabel data-test={dataTest && `${dataTest}-status`} value={value.status} />
    </Space>
  );
};

export default TransactionAmount;
